import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router';

import { SearchHitCarousel } from '#/components/SearchHitCarousel/SearchHitCarousel';
import { UnpublishedListing } from '#/components/UnpublishedListing/UnpublishedListing';
import { useI18n } from '#/i18n/hooks/useI18n';
import { trackViewListing } from '#/modules/listing/analytics/trackViewListing';
import type { ApiUnpublishedJob } from '#/modules/listing/jobs/api/types/ApiUnpublishedJob';
import { useSearchContextHeader } from '#/modules/search/containers/SearchContextHeader/useSearchContextHeader';
import { getParsedSearchQuery } from '#/modules/search/helpers/getParsedSearchQuery';
import { searchGetJobsRoutePath } from '#/modules/search/jobs/routing/searchGetJobsRoutePath';
import type { SearchPageLocationState } from '#/modules/search/pages/Search/types/SearchPageLocationState';

import { getUnpublishedJobPageSeeMoreText } from './getUnpublishedJobPageSeeMoreText';

type Props = {
  unpublishedJob: ApiUnpublishedJob;
};

export function UnpublishedJobPage({ unpublishedJob }: Props) {
  const { i18n, locale } = useI18n();

  const { searchContext } = useSearchContextHeader({
    currentObjectId: unpublishedJob.id,
  });

  const { hasAts, listingName, id, org } = unpublishedJob;

  useEffect(() => {
    trackViewListing({
      status: 'unpublished',
      hasAts,
      listingName,
      listingId: id,
      listingType: 'JOB',
      orgId: org ? org.id : undefined,
      orgName: org ? org.name : undefined,
      orgType: org ? org.orgType : undefined,
    });
  }, [hasAts, id, listingName, org]);

  const navigate = useNavigate();
  const search = useCallback(
    (query: string) => {
      const { parsedQuery, locationType, remoteKeywords } =
        getParsedSearchQuery({ query, locale });

      void navigate(
        searchGetJobsRoutePath({
          locale,
          q: parsedQuery,
          filtersByName: { locationType },
          embedSearchWidgetId: null,
        }),
        {
          state: {
            searchFrom: 'Unpublished Job Page',
            remoteKeywords,
          } satisfies SearchPageLocationState,
        },
      );
    },
    [locale, navigate],
  );

  // @TODO Add new recommender
  const jobRecommendations: never[] = [];

  const seeMoreText = getUnpublishedJobPageSeeMoreText({
    i18n,
    orgUrl: unpublishedJob.org
      ? `${unpublishedJob.org.url[locale]}#opportunities`
      : undefined,
    orgPublishedJobsCount: unpublishedJob.orgPublishedJobsCount ?? 0,
  });

  return (
    <UnpublishedListing
      searchContext={searchContext}
      footerTitle={i18n('Search Jobs')}
      seeMoreText={seeMoreText}
      searchFormAnalyticsTitle="[main] View Job Page (Unpublished) - search"
      onSubmit={({ values }) => search(values.query)}
      exploreSection={
        jobRecommendations &&
        jobRecommendations.length > 0 && (
          <SearchHitCarousel
            headline={i18n('Explore Similar Jobs on Idealist')}
            results={jobRecommendations}
            seeAll={{
              /* TRANSLATORS: Link to job search */
              text: i18n('See all jobs'),
              action: {
                type: 'link',
                url: searchGetJobsRoutePath({
                  locale,
                  embedSearchWidgetId: null,
                }),
              },
            }}
            listingType="JOB"
            carouselLocation="Job Listing"
            fromListingId={unpublishedJob.id}
          />
        )
      }
    />
  );
}
